import { useEffect, useState } from "react";
import { Eye, History, FileDown } from "lucide-react";
import { api, download, errCode } from "../lib/api";
import { useLang } from "../lib/i18n";
import { fmtDateTime } from "../lib/time";
import { AuthImage } from "./Common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export const LabelPreviewButton = ({ batchId, testid, small }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const src = `/labels/sample.png${batchId ? `?batch_id=${batchId}` : ""}`;
  return (
    <>
      <button type="button" className={`btn-secondary ${small ? "btn-sm" : ""}`} onClick={() => setOpen(true)} data-testid={testid}>
        <Eye size={16} />{t("preview")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("label_preview")}</DialogTitle></DialogHeader>
          {open && <AuthImage src={src} alt="label preview" testid="label-preview-image"
            className="w-full aspect-[2/3] object-contain border-2 border-slate-300 rounded-lg bg-white shadow-sm" />}
          <p className="text-xs text-slate-600">{t("preview_hint")}</p>
        </DialogContent>
      </Dialog>
    </>
  );
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Starts a combined print job, polls progress and downloads one PDF.
export const usePrintJob = () => {
  const { te } = useLang();
  const [prog, setProg] = useState(null);
  const start = async (batchIds) => {
    setProg({ done: 0, total: 0, phase: "render" });
    try {
      let { data: job } = await api.post("/print-jobs", { batch_ids: batchIds });
      setProg({ done: 0, total: job.total, phase: "render" });
      window.dispatchEvent(new Event("print-jobs-changed"));
      while (job.status === "running") {
        await sleep(2000);
        job = (await api.get(`/print-jobs/${job.id}`)).data;
        setProg({ done: job.done, total: job.total, phase: "render" });
      }
      window.dispatchEvent(new Event("print-jobs-changed"));
      if (job.status !== "done") throw new Error("print_failed");
      setProg({ done: job.total, total: job.total, phase: "download" });
      await download(`/print-jobs/${job.id}/file`, job.filename);
      return job;
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(te(e.message === "print_failed" ? "print_failed" : errCode(e)));
      return null;
    } finally { setProg(null); }
  };
  return { start, prog };
};

export const PrintHistory = () => {
  const { t, te, lang } = useLang();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    const load = () => api.get("/print-jobs").then((r) => setRows(r.data)).catch(() => {});
    load();
    window.addEventListener("print-jobs-changed", load);
    return () => window.removeEventListener("print-jobs-changed", load);
  }, []);
  const get = async (j) => {
    setBusy(j.id);
    try { await download(`/print-jobs/${j.id}/file`, j.filename); }
    catch (e) { const { toast } = await import("sonner"); toast.error(te(errCode(e))); }
    setBusy("");
  };
  return (
    <section className="card" data-testid="print-history-section">
      <h2 className="font-bold text-lg p-4 border-b flex items-center gap-2"><History size={20} />{t("print_history")}</h2>
      <div className="overflow-x-auto"><table className="tbl w-full" data-testid="print-history-table">
        <thead><tr><th>{t("date")}</th><th>{t("batch")}</th><th>{t("nav_labels")}</th><th>{t("created_by")}</th><th>{t("status")}</th><th></th></tr></thead>
        <tbody>{rows.map((j) => (
          <tr key={j.id} data-testid={`print-history-row-${j.id}`}>
            <td className="text-xs whitespace-nowrap">{fmtDateTime(j.created_at, lang)}</td>
            <td className="font-mono text-xs">{j.batch_nos.length > 1 ? `${j.batch_nos[0]} → ${j.batch_nos[j.batch_nos.length - 1]} (${j.batch_nos.length})` : j.batch_nos[0]}</td>
            <td className="font-mono">{j.total.toLocaleString("es-CL")}</td>
            <td className="text-xs">{j.created_by}</td>
            <td className="text-xs font-bold">{t(`job_${j.status}`)}{j.size ? <span className="font-normal text-slate-500"> · {(j.size / 1048576).toFixed(1)} MB</span> : null}</td>
            <td>{j.status === "done" && <button className="btn-secondary btn-sm" disabled={busy === j.id} onClick={() => get(j)} data-testid={`print-history-download-${j.id}`}>
              <FileDown size={16} />{busy === j.id ? "…" : t("download")}</button>}</td>
          </tr>))}
          {!rows.length && <tr><td colSpan={6} className="text-slate-500">{t("none")}</td></tr>}</tbody>
      </table></div>
    </section>
  );
};

export const PrintProgress = ({ prog }) => {
  const { t } = useLang();
  if (!prog) return null;
  const pct = prog.total ? Math.round((prog.done / prog.total) * 100) : 0;
  return (
    <div className="space-y-1" data-testid="print-progress">
      <p className="font-mono text-sm font-bold">
        {prog.phase === "download" ? t("downloading_file") : `${t("printing_progress")} ${prog.done.toLocaleString("es-CL")} / ${prog.total.toLocaleString("es-CL")}`}
      </p>
      <div className="h-2 rounded bg-slate-200 overflow-hidden"><div className="h-full bg-[#C8102E] transition-[width] duration-500" style={{ width: `${prog.phase === "download" ? 100 : pct}%` }} /></div>
    </div>
  );
};
